import type { AutofillSource } from "../zod/investor/investor";
import {
  BANK_ACCOUNT_TYPE,
  BELONGS_TO,
  GENDER,
  NOMINEE_RELATIONSHIP,
  IDENTITY_TYPE_CHOICES,
  INCOME_RANGE,
  OCCUPATION,
  PEP_STATUS,
  SOURCE_OF_WEALTH,
} from "../constants";
import {
  SecondHolderContactDetailsSchema,
  SecondHolderOtherInfoSchema,
  SecondHolderPersonalInfoSchema,
} from "../zod/investor/second-holder";
import {
  AddSecondHolderFormData,
  InvestorContactInfo,
  InvestorProfileDetails,
} from "../types";
import { convertSnakeCaseToTitleCase } from "./string";

export const getUserGuidQueryString = (userGuid: string) =>
  userGuid ? `?user_guid=${userGuid}` : "";

export function getValue(field: string, value: string) {
  if (field === "identity_type") {
    const identityType = IDENTITY_TYPE_CHOICES.find((o) => o.value === value);
    return identityType?.label;
  }

  if (field === "gender") {
    const gender = GENDER.find((o) => o.value === value);
    return gender?.label;
  }

  if (field === "belongs_to") {
    const belongsTo = BELONGS_TO.find((o) => o.value === value);
    return belongsTo?.label;
  }

  if (field === "type") {
    const bankAccountType = BANK_ACCOUNT_TYPE.find((o) => o.value === value);
    return bankAccountType?.label;
  }

  if (field === "occupation") {
    const occupation = OCCUPATION.find((o) => o.value === value);
    return occupation?.label;
  }

  if (field === "income_slab") {
    const incomeSlab = INCOME_RANGE.find((o) => o.value === value);
    return incomeSlab?.label;
  }

  if (field === "source_of_wealth") {
    const sourceOfWealth = SOURCE_OF_WEALTH.find((o) => o.value === value);
    return sourceOfWealth?.label;
  }

  if (field === "pep_details") {
    const pepDetails = PEP_STATUS.find((o) => o.value === value);
    return pepDetails?.label;
  }

  if (field === "relationship") {
    const relationship = NOMINEE_RELATIONSHIP?.find((o) => o.value === value);
    return relationship?.label;
  }

  if (field === "identity_type") {
    const identityType = IDENTITY_TYPE_CHOICES.find((o) => o.value === value);
    return identityType?.label;
  }

  if (field === "phone_belongs_to" || field === "email_belongs_to") {
    const belongsTo = BELONGS_TO.find((o) => o.value === value);
    return belongsTo?.label;
  }

  if (field === "applicable_percentage") {
    return `${value}%`;
  }

  return value;
}

export function getFieldLabel(field: string) {
  if (field === "identity_type") {
    return "Identity Type";
  }
  if (field === "pan") {
    return "PAN";
  }
  if (field === "gender") {
    return "Gender";
  }
  if (field === "belongs_to") {
    return "Belongs To";
  }
  if (field === "type") {
    return "Type";
  }
  if (field === "occupation") {
    return "Occupation";
  }
  if (field === "income_slab") {
    return "Income Slab";
  }

  if (field === "source_of_wealth") {
    return "Source of Wealth";
  }
  if (field === "pep_details") {
    return "Political exposure";
  }
  if (field === "relationship") {
    return "Relation";
  }

  if (field === "date_of_birth") {
    return "Date of birth";
  }

  if (field === "country_of_birth") {
    return "Country of birth";
  }

  if (field === "ifsc_code") {
    return "IFSC code";
  }

  if (field === "line1") {
    return "Address line 1";
  }

  if (field === "line2") {
    return "Address line 2";
  }

  if (field === "line3") {
    return "Address line 3";
  }

  if (field === "postal_code") {
    return "Postal code";
  }

  return convertSnakeCaseToTitleCase(field);
}

export const nomineeFieldOrderMap: Record<string, number> = {
  name: 1,
  relationship: 2,
  identity_type: 3,
  identity_number: 4,
  email: 5,
  mobile: 6,
  date_of_birth: 7,
  guardian_name: 8,
  guardian_pan: 9,
  address_line_1: 10,
  address_line_2: 11,
  address_line_3: 12,
  city: 13,
  pin_code: 14,
  country: 15,
  applicable_percentage: 16,
};

export function splitHolderPhone(phone?: string | null) {
  if (!phone) return { isd: "+91", number: "" };
  const digits = phone.replace(/\D/g, "");
  const number = digits.length > 10 ? digits.slice(-10) : digits;
  return { isd: "+91", number };
}

// A joint holder's own phone/email as contact details, since the
// account-level contact is shared by both holders
export function getHolderContactInfo(holder?: InvestorProfileDetails) {
  if (!holder) return undefined;
  const { isd, number } = splitHolderPhone(holder.phone_number);
  return {
    isd,
    number,
    email: holder.email ?? "",
    phone_belongs_to: "self",
    email_belongs_to: "self",
  } as InvestorContactInfo;
}

export function findPrimaryHolder(profiles?: InvestorProfileDetails[]) {
  if (!profiles?.length) return undefined;
  return (
    profiles.find((profile) => profile.holder_role === "primary") ?? profiles[0]
  );
}

export function findSecondHolder(profiles?: InvestorProfileDetails[]) {
  if (!profiles?.length) return undefined;
  const secondary = profiles.find(
    (profile) => profile.holder_role === "secondary"
  );
  if (secondary) return secondary;
  if (profiles.some((profile) => profile.holder_role)) return undefined;
  return profiles.length > 1 ? profiles[1] : undefined;
}

export function secondHolderToFormData(
  secondHolder?: InvestorProfileDetails
): AddSecondHolderFormData {
  if (!secondHolder) return {};

  const phone = splitHolderPhone(secondHolder.phone_number);

  return {
    investor_profile_guid: secondHolder.investor_profile_guid,
    name: secondHolder.name ?? undefined,
    pan: secondHolder.pan ?? undefined,
    gender: secondHolder.gender ?? undefined,
    date_of_birth: secondHolder.date_of_birth ?? undefined,
    email: secondHolder.email ?? undefined,
    number: phone.number || undefined,
    isd: phone.isd,
    phone_belongs_to: "self",
    email_belongs_to: "self",
    occupation: secondHolder.occupation ?? undefined,
    source_of_wealth: secondHolder.source_of_wealth ?? undefined,
    income_slab: secondHolder.income_slab ?? undefined,
    pep_details: secondHolder.pep_details ?? undefined,
    kyc_status: secondHolder.kyc_status,
    kyc_status_remark: secondHolder.kyc_status_remark,
    has_other_client: secondHolder.has_other_client,
  };
}

export const SECOND_HOLDER_STEPS: {
  id: string;
  completed: (data: AddSecondHolderFormData) => boolean;
}[] = [
  {
    id: "personal-info",
    completed: (data) =>
      SecondHolderPersonalInfoSchema.safeParse(data).success &&
      (data.kyc_status === "S" || data.kyc_status === "U"),
  },
  {
    id: "contact-details",
    completed: (data) =>
      SecondHolderContactDetailsSchema.safeParse({
        isd: data.isd ?? "+91",
        phone_belongs_to: data.phone_belongs_to ?? "self",
        email_belongs_to: data.email_belongs_to ?? "self",
        number: data.number,
        email: data.email,
      }).success,
  },
  {
    id: "other-info",
    completed: (data) => SecondHolderOtherInfoSchema.safeParse(data).success,
  },
  {
    id: "notify-holders",
    completed: () => false,
  },
];

export function isSecondHolderDetailsIncomplete(
  profiles?: InvestorProfileDetails[]
) {
  const data = secondHolderToFormData(findSecondHolder(profiles));
  return SECOND_HOLDER_STEPS.some(
    (step) => step.id !== "notify-holders" && !step.completed(data)
  );
}

function normalizeHolderPhoneNumber(isd?: string, number?: string) {
  if (!number) return undefined;
  if (number.startsWith("+")) return number;
  return `${isd || "+91"}${number}`;
}

export function buildHolderProfilePayload(
  clientId: number,
  data: AddSecondHolderFormData
) {
  const phoneNumber = normalizeHolderPhoneNumber(data.isd, data.number);

  return {
    client_id: clientId,
    holder_role: "secondary" as const,
    investor_profile_guid: data.investor_profile_guid,
    tax_status: "resident_individual" as const,
    name: data.name,
    date_of_birth: data.date_of_birth,
    gender: data.gender,
    pan: data.pan,
    email: data.email,
    phone_number: phoneNumber,
    occupation: data.occupation,
    source_of_wealth: data.source_of_wealth,
    income_slab: data.income_slab,
    pep_details: data.pep_details,
  };
}

export function formatHolderMobile(isd?: string, number?: string) {
  if (!number) return "—";
  if (number.startsWith("+")) return number;
  return `${isd || "+91"}${number}`;
}

// Where an onboarding form's prefilled data came from
export function getAutofillSourceMessage(
  name?: string,
  source?: AutofillSource | null
) {
  const owner = `${name ?? "the client"}'s`;
  switch (source) {
    case "HYPERVERGE":
      return `Data fetched from ${owner} KYC application.`;
    case "MF_CENTRAL":
      return `Data fetched from ${owner} MFCentral records.`;
    default:
      return `Data fetched from ${owner} KYC application.`;
  }
}
